import { create } from "zustand";

import type { UserProgressionStateModel } from "@/api/generated/models";

/** Screens inside the progression's authentication step, which the backend sees as one step. */
export type AuthScreen = "phone" | "code" | "signing_in";

type ActiveSetup = {
  conversationId: string;
  /** The `pre_async_requirements` event that opened the panel. */
  eventId: string;
  /** The `pre_async` progression the panel walks; null until it has been fetched. */
  progression: UserProgressionStateModel | null;
  authScreen: AuthScreen;
};

type PreAsyncSetupStore = {
  active: ActiveSetup | null;
  phone: string;
  /** The event whose panel last closed, so it is not reopened before the patient's message lands. */
  closedEventId: string | null;
  start: (conversationId: string, eventId: string) => void;
  setProgression: (progression: UserProgressionStateModel) => void;
  goToAuthScreen: (authScreen: AuthScreen) => void;
  setPhone: (phone: string) => void;
  clear: () => void;
};

/**
 * The pre-async setup panel's state. In memory only, like the full-screen sign-in's: it outlives the
 * conversation remounting when the Clerk session swaps, and after a reload the backend progression
 * says which step to resume at.
 */
export const usePreAsyncSetupStore = create<PreAsyncSetupStore>()((set) => ({
  active: null,
  phone: "",
  closedEventId: null,
  // Restarting for the event already in progress keeps its place, e.g. across the sign-in remount.
  start: (conversationId, eventId) =>
    set((state) => {
      if (state.active?.eventId === eventId) return state;
      if (state.closedEventId === eventId) return state;

      return {
        active: {
          conversationId,
          eventId,
          progression: null,
          authScreen: "phone",
        },
      };
    }),
  setProgression: (progression) =>
    set((state) =>
      state.active ? { active: { ...state.active, progression } } : state,
    ),
  goToAuthScreen: (authScreen) =>
    set((state) =>
      state.active ? { active: { ...state.active, authScreen } } : state,
    ),
  setPhone: (phone) => set({ phone }),
  clear: () =>
    set((state) => ({
      active: null,
      phone: "",
      closedEventId: state.active?.eventId ?? state.closedEventId,
    })),
}));
